import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const SuperadminDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [branches, setBranches] = useState({});
  const [loadingBranches, setLoadingBranches] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  const API_BASE_URL = 'https://greenyellow-nightingale-241896.hostingersite.com/api';

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCurrentUser({
          id: decoded.id || decoded.userId || decoded._id,
          role: decoded.role,
          name: decoded.name || 'Super Admin'
        });
      } catch (error) {
        console.error('Error decoding JWT:', error);
      }
    }
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No authentication token found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/companies`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      // Filter only parent companies (not branches)
      const parentCompanies = response.data.filter(company => !company.isBranch);
      setCompanies(parentCompanies);
      // Fetch branches for all companies after companies are loaded
      const token2 = localStorage.getItem('authToken');
      if (token2) {
        const branchesObj = {};
        await Promise.all(
          parentCompanies.map(async (company) => {
            try {
              const response = await axios.get(
                `${API_BASE_URL}/companies/${company._id}/branches`,
                {
                  headers: { Authorization: `Bearer ${token2}` },
                  timeout: 15000
                }
              );
              branchesObj[company._id] = response.data.branches || [];
            } catch (error) {
              branchesObj[company._id] = [];
            }
          })
        );
        setBranches(branchesObj);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError(`Failed to fetch companies: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      setLoadingBranches(prev => ({ ...prev, [companyId]: true }));

      const response = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/branches`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        }
      );

      setBranches(prev => ({
        ...prev,
        [companyId]: response.data.branches || []
      }));
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches(prev => ({
        ...prev,
        [companyId]: []
      }));
    } finally {
      setLoadingBranches(prev => ({ ...prev, [companyId]: false }));
    }
  };

  const toggleCompany = async (companyId) => {
    if (expandedCompany === companyId) {
      setExpandedCompany(null);
    } else {
      setExpandedCompany(companyId);
      // Fetch branches if not already loaded
      if (!branches[companyId]) {
        await fetchBranches(companyId);
      }
    }
  };

  const deleteCompany = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !window.confirm('Are you sure you want to delete this company and all its branches?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompanies(prev => prev.filter(company => company._id !== companyId));
      setBranches(prev => {
        const newBranches = { ...prev };
        delete newBranches[companyId];
        return newBranches;
      });

      if (expandedCompany === companyId) {
        setExpandedCompany(null);
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Error deleting company: ' + error.message);
    }
  };

  const deleteBranch = async (companyId, branchId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !window.confirm('Are you sure you want to delete this branch?')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/companies/${companyId}/branches/${branchId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setBranches(prev => ({
        ...prev,
        [companyId]: prev[companyId].filter(branch => branch._id !== branchId)
      }));
    } catch (error) {
      console.error('Error deleting branch:', error);
      alert('Error deleting branch: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {currentUser?.name}!
              </h1>
              <p className="text-gray-600 mt-2">
                Manage all companies and their branches
              </p>
            </div>

            <div className="mt-4 md:mt-0">
              <div className="bg-white px-4 py-2 rounded-lg border border-gray-300 shadow-sm">
                <p className="text-sm text-gray-600">Role</p>
                <p className="font-semibold text-indigo-600 capitalize">{currentUser?.role}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
            <button
              onClick={fetchCompanies}
              className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Stats Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Companies</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{companies.length}</p>
              </div>
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Branches</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {Object.values(branches).reduce((acc, branchList) => acc + branchList.length, 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Plans</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {companies.filter(c => c.businessSubscriptionPlan).length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Companies List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Companies & Branches</h2>
            <button
              onClick={fetchCompanies}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
          </div>

          {companies.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="mt-4 text-gray-600">No companies found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {companies.map((company) => (
                <div key={company._id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Company Header */}
                  <div className="bg-gray-50 p-4 flex items-center justify-between hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-4 flex-1">
                      <button
                        onClick={() => toggleCompany(company._id)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <svg
                          className={`w-5 h-5 text-gray-600 transition-transform ${expandedCompany === company._id ? 'rotate-90' : ''
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <span className="text-indigo-600 font-bold text-lg">
                          {company.businessName?.charAt(0) || 'C'}
                        </span>
                      </div>

                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg">{company.businessName}</h3>
                        <div className="flex items-center space-x-4 mt-1">
                          <p className="text-sm text-gray-600">{company.businessEmail}</p>
                          <p className="text-sm text-gray-600">{company.businessPhone}</p>
                          {/* {company.businessSubscriptionPlan && (
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                              {company.businessSubscriptionPlan}
                            </span>
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                        {branches[company._id]?.length || 0} branches
                      </span>
                      {/* <button
                        onClick={() => deleteCompany(company._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button> */}
                    </div>
                  </div>

                  {/* Branches Section */}
                  {expandedCompany === company._id && (
                    <div className="bg-white p-4 border-t border-gray-200">
                      {loadingBranches[company._id] ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          <p className="ml-3 text-gray-600">Loading branches...</p>
                        </div>
                      ) : branches[company._id]?.length > 0 ? (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-gray-700 mb-3">Branches:</h4>
                          {branches[company._id].map((branch) => (
                            <div
                              key={branch._id}
                              className="ml-8 p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                  <span className="text-green-600 font-semibold">
                                    {branch.businessName?.charAt(0) || 'B'}
                                  </span>
                                </div>
                                <div>
                                  <h5 className="font-medium text-gray-900">{branch.businessName}</h5>
                                  <div className="flex items-center space-x-3 mt-1">
                                    <p className="text-sm text-gray-600">{branch.businessEmail}</p>
                                    <p className="text-sm text-gray-600">{branch.businessPhone}</p>
                                  </div>
                                  {branch.address && (
                                    <p className="text-xs text-gray-500 mt-1">{branch.address}</p>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => deleteBranch(company._id, branch._id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          <p className="mt-2">No branches found for this company</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperadminDashboard;

